import api from "../../../shared/services/api";

/** Limites de tamanho por tipo de mídia (em bytes) — espelha os limites da Meta */
export const MIME_LIMITS = {
  image:    5  * 1024 * 1024,
  video:    16 * 1024 * 1024,
  audio:    16 * 1024 * 1024,
  document: 100 * 1024 * 1024,
};

/** Tipos MIME aceitos por categoria (valor para atributo accept do input file) */
export const MIME_ACEITOS = {
  image:    "image/jpeg,image/png",
  video:    "video/mp4,video/3gp",
  audio:    "audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg",
  document: "*/*",
};

/**
 * Faz upload de arquivo de mídia para o backend, que repassa à Meta.
 * Retorna media_id pronto para uso no envio de mensagem.
 *
 * @param {number} contaId
 * @param {'image'|'video'|'audio'|'document'} tipo
 * @param {File} arquivo — objeto File do browser
 * @param {(progress: number) => void} [onProgress] — callback de progresso 0–100
 * @returns {Promise<{ media_id: string, mime_type: string, file_size: number }>}
 */
export async function upload(contaId, tipo, arquivo, onProgress = null) {
  const limite = MIME_LIMITS[tipo];
  if (limite && arquivo.size > limite) {
    const mb = Math.round(limite / 1024 / 1024);
    throw new Error(`Arquivo muito grande. Limite para ${tipo}: ${mb} MB.`);
  }

  const formData = new FormData();
  formData.append("file", arquivo);
  formData.append("tipo", tipo);

  const config = {};
  if (onProgress) {
    config.onUploadProgress = (evt) => {
      if (evt.total) {
        onProgress(Math.round((evt.loaded * 100) / evt.total));
      }
    };
  }

  const resp = await api.post(
    `/whatsapp/meta/contas/${contaId}/midia/upload`,
    formData,
    config,
  );

  return resp.data?.data ?? resp.data;
}

/**
 * Retorna a URL autenticada para servir mídia.
 * Como o frontend usa withCredentials=true (cookies), <img src> e <video src>
 * funcionam diretamente pois o browser envia cookies automaticamente na
 * mesma origem. Para origem cruzada, usar downloadBlob().
 *
 * @param {number} contaId
 * @param {number|string} mid — id numérico da row em meta_whatsapp_midia
 * @returns {string} URL relativa ou absoluta pronta para uso em src/href
 */
export function getMediaUrl(contaId, mid) {
  const base = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");
  return `${base}/whatsapp/meta/contas/${contaId}/midia/${mid}`;
}

/**
 * Faz download de mídia via fetch autenticado e retorna um blob URL.
 * Usar quando <img src> não for suficiente (ex: Authorization header necessário).
 * O blob URL deve ser revogado com URL.revokeObjectURL() após o uso.
 *
 * @param {number} contaId
 * @param {number|string} mid
 * @returns {Promise<string>} blob URL temporária
 */
export async function downloadBlob(contaId, mid) {
  const resp = await api.get(
    `/whatsapp/meta/contas/${contaId}/midia/${mid}`,
    { responseType: "blob" },
  );
  return URL.createObjectURL(resp.data);
}
